import type { Point2D } from '../types'

/**
 * Ограничение значения промежутком от min до max
 *
 * @param min минимально допустимое значение
 * @param max максимально допустимое значение
 * @param value ограничиваемое значение
 * @returns value, обрезанное границами min и max
 */
export const clamp = (value: number, min: number, max: number) =>
  Math.max(Math.min(value, max), min)

export const floorByMod = (value: number, mod: number): number => Math.floor(value / mod) * mod

const logWithBase = (logarithmable: number, base: number): number =>
  Math.log(logarithmable) / Math.log(base)

/**
 * Степенное масштабирование.
 *
 * Для диапазона вычисляет шаг по основанию.

```text
    Для base = 10 и stepScale = 10 шаг равен:
    range       step
    0..1        0.1
    1..10       1
    10..100     10
    100..1000   100

    Для base = 10 и stepScale = 2 шар равен:
    range       step
    0.5..5      0.1
    5..50       1
    50..500     10
    500..5000   100
```

 * @param base основание
 * @param stepScale масштабирование диапазона
 * @param range диапазон, для которого вычисляется шаг
 * @returns
 */
export const floorLogScale = (base: number, stepScale: number, range: number): number =>
  Math.pow(base, Math.floor(logWithBase(Math.abs(range) * stepScale, base)) - 1)

export const roundToPrecision = (value: number, digits: number) => {
  const exponent = 10 ** digits
  return Math.round(value * exponent) / exponent
}
/**
 * Утилита для округления до первых ненулевых значений (0.00000012323121 => 0.00000012).
 * @param value - число, которое будем округлять
 * @param precision - настройка количества сокращаемых символов после первого ненулевого,
 *                    чем меньше precision - тем больше останется сивмолов
 */
export const roundToNonZero = (value: number, precision: number = 5): number => {
  if (value === 0) return value

  if (Math.abs(value) > 1) return +value.toFixed(2)

  return +value.toFixed(1 - Math.floor(Math.log(value) / Math.log(precision)))
}

export const floor2D = (point: Point2D): Point2D => ({
  x: Math.floor(point.x),
  y: Math.floor(point.y),
})

export const ceil2D = (point: Point2D): Point2D => ({
  x: Math.ceil(point.x),
  y: Math.ceil(point.y),
})

export const round2D = (point: Point2D): Point2D => ({
  x: Math.round(point.x),
  y: Math.round(point.y),
})

export const trunc2D = (point: Point2D): Point2D => ({
  x: Math.trunc(point.x),
  y: Math.trunc(point.y),
})
